import React, { useState } from 'react';
import { Control, Controller, FieldPath, FieldValues } from 'react-hook-form';
import { ErrorTooltip } from '@retail/components';
import { BaseTextFieldProps, IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { Remove } from '@mui/icons-material';
import { useAppTFunction } from '@retail/app/i18n';

interface Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues
> {
  name: TName;
  control: Control<TFieldValues, any, TTransformedValues>;
  size?: BaseTextFieldProps['size'];
  sx?: BaseTextFieldProps['sx'];
  disabled?: BaseTextFieldProps['disabled'];
  onRemove?: () => void;
}

export const RemovablePriceInput = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues
>({
  name,
  control,
  onRemove,
  ...rest
}: Props<TFieldValues, TName, TTransformedValues>) => {
  const t = useAppTFunction();
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const showCloseButton = isFocused || isHovered;

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <ErrorTooltip title={fieldState.error?.message} arrow>
          <TextField
            {...field}
            error={!!fieldState.error}
            onFocus={(e) => {
              setIsFocused(true);
              field.onBlur();
            }}
            onBlur={() => setIsFocused(false)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    {showCloseButton && onRemove ? (
                      <Tooltip title={t('rounding.actions.removeTargetEnding')}>
                        <IconButton
                          size="small"
                          onClick={onRemove}
                          edge="end"
                          sx={{ '&:hover': { color: 'error.main' } }}
                        >
                          <Remove />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      t('products.prices.price', { price: '' })
                    )}
                  </InputAdornment>
                ),
              },
            }}
            {...rest}
          />
        </ErrorTooltip>
      )}
    />
  );
};
